import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  // DollarCircleOutlined,
  EyeOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import {
  Button,
  AutoComplete,
  Slider,
  Spin,
} from "antd";
import { toast } from "react-toastify";

import useGyngerCheckoutPayment from "../hooks/useGyngerCheckoutPayment";
import ReviewOneTimeTopUpModal from "../subscription/components/ReviewOneTimeTopUpModal";
import SubscriptionPaymentSummary from "./SubscriptionPaymentSummary";
import SuccessModal from "./SuccessModal";
import UsageMetricsPaymentSummary from "./UsageMetricsPaymentSummary";
// import UsageMetricsPlanOffers from "./UsageMetricsPlanOffers";
import UsageMetricsTestsDrawer from "./UsageMetricsTestsDrawer";

import {
  applyPrefillCreditsToQuantities,
  buildRechargeCredits,
  calculatePlanBuilderTotals,
  clearPlanBuilderPrefillCredits,
  getPlanBuilderPrefillCredits,
  mapUsageCategoriesToPlanBuilder,
} from "../utils";

import { getUsageCategories } from "../../../../actions/usage";

import microscopeIcon from "../../../../assets/images/subscriptions/microscope-line.svg";
import reactjsIcon from "../../../../assets/images/subscriptions/reactjs-line.svg";

import "./UsageMetricsPlanBuilder.css";

const CATEGORY_ICONS = {
  microbiology: <img src={microscopeIcon} alt="" aria-hidden />,
  chemistry: <img src={reactjsIcon} alt="" aria-hidden />,
};

const QUANTITY_STEP = 10;

const getQuantityStep = (maxQuantity) => (
  maxQuantity > 0 && maxQuantity < QUANTITY_STEP ? 1 : QUANTITY_STEP
);

const getQuantityOptions = (maxQuantity) => {
  if (maxQuantity <= 0) return [{ value: "0" }];
  const step = getQuantityStep(maxQuantity);
  const options = [];
  for (let value = 0; value <= maxQuantity; value += step) {
    options.push({ value: String(value) });
  }
  const lastValue = Number(options[options.length - 1]?.value);
  if (lastValue !== maxQuantity) {
    options.push({ value: String(maxQuantity) });
  }
  return options;
};

const getSliderMarks = (maxQuantity) => {
  if (!maxQuantity || maxQuantity <= 0) return {};
  const unit = maxQuantity / 8;
  return [1, 3, 5, 7].reduce((marks, multiplier) => {
    const value = Math.round(unit * multiplier);
    if (value > 0 && value < maxQuantity) {
      marks[value] = "";
    }
    return marks;
  }, {});
};

const clampQuantity = (value, maxQuantity) => {
  const parsed = Number.parseInt(String(value ?? ""), 10);
  const cap = Number(maxQuantity);
  if (Number.isNaN(parsed) || !Number.isFinite(cap) || cap <= 0) return 0;
  return Math.min(Math.max(parsed, 0), cap);
};

export default function UsageMetricsPlanBuilder({
  mode,
  onBack,
  onTopUp,
  onAddToSubscription,
  onRechargeSuccess,
}) {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [quantities, setQuantities] = useState({});
  const [quantityDrafts, setQuantityDrafts] = useState({});
  const [testsDrawerOpen, setTestsDrawerOpen] = useState(false);
  const [reviewModal, setReviewModal] = useState({ open: false, variant: "topup" });

  const handlePrepaidCheckoutComplete = useCallback(async (response) => {
    const rechargeCredits = buildRechargeCredits(quantities, categories);
    const creditedTests = rechargeCredits.reduce(
      (sum, credit) => sum + Number(credit.quantity),
      0,
    );

    if (onRechargeSuccess) {
      onRechargeSuccess({ ...response, creditedTests });
    } else if (onBack) {
      onBack();
    }
  }, [categories, onBack, onRechargeSuccess, quantities]);

  const {
    isCheckoutLoading,
    paymentUrl,
    showSuccessModal: showPrepaidSuccessModal,
    handleSuccessClose: handlePrepaidSuccessClose,
    showFailedModal: showPrepaidFailedModal,
    handleFailedClose: handlePrepaidFailedClose,
    startGyngerCheckout,
    resetCheckout,
  } = useGyngerCheckoutPayment({
    onComplete: handlePrepaidCheckoutComplete,
  });

  useEffect(() => () => {
    resetCheckout();
  }, [resetCheckout]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const loadCategories = async () => {
      setCategoriesLoading(true);
      const response = await getUsageCategories({
        signal: controller.signal,
      });

      if (!isMounted || response.canceled) return;

      if (!response.success) {
        toast.error(response.message);
        setCategoriesLoading(false);
        return;
      }

      const mappedCategories = mapUsageCategoriesToPlanBuilder(response.results);
      const prefillCredits = getPlanBuilderPrefillCredits();
      setCategories(mappedCategories);
      setQuantities(applyPrefillCreditsToQuantities(mappedCategories, prefillCredits));
      clearPlanBuilderPrefillCredits();
      setCategoriesLoading(false);
    };

    loadCategories();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const updateQuantity = (testKey, quantity, maxQuantity) => {
    const nextQuantity = clampQuantity(quantity, maxQuantity);
    setQuantities((current) => ({
      ...current,
      [testKey]: nextQuantity,
    }));
    setQuantityDrafts((current) => {
      if (current[testKey] == null) return current;
      const next = { ...current };
      delete next[testKey];
      return next;
    });
  };

  const handleQuantityDraftChange = (testKey, rawValue) => {
    const digitsOnly = String(rawValue ?? "").replace(/[^\d]/g, "");
    setQuantityDrafts((current) => ({
      ...current,
      [testKey]: digitsOnly,
    }));
  };

  const commitQuantityDraft = (testKey, maxQuantity) => {
    const draft = quantityDrafts[testKey];
    if (draft == null) return;
    updateQuantity(testKey, draft, maxQuantity);
  };

  const { total, totalCredits } = useMemo(
    () => calculatePlanBuilderTotals(quantities, categories),
    [quantities, categories],
  );

  const reviewCategories = useMemo(() => (
    categories.map((category) => ({
      key: category.key,
      name: category.name,
      iconClass: `ReviewOneTimeTopUpModal__CategoryIcon--${category.key}`,
      items: category.tests
        .filter((test) => quantities[test.key] > 0)
        .map((test) => ({
          key: test.key,
          name: test.name,
          quantity: quantities[test.key],
          rate: test.unitPrice,
        })),
    })).filter((category) => category.items.length > 0)
  ), [quantities, categories]);

  const openReviewModal = (variant) => {
    const credits = buildRechargeCredits(quantities, categories);
    if (credits.length === 0) {
      toast.error("Select at least one test quantity to continue.");
      return false;
    }

    setReviewModal({ open: true, variant });
    return true;
  };

  const handlePrepaidContinue = () => {
    startGyngerCheckout({
      amount: total,
      credits: buildRechargeCredits(quantities, categories),
    });
  };

  const handleTopUp = () => {
    if (!openReviewModal("topup")) {
      return;
    }

    if (onTopUp) {
      onTopUp();
    }
  };

  const handleAddToSubscription = () => {
    if (!openReviewModal("subscription")) {
      return;
    }

    if (onAddToSubscription) {
      onAddToSubscription();
    }
  };

  const closeReviewModal = () => {
    setReviewModal((current) => ({ ...current, open: false }));
  };

  const handleReviewPay = async (response) => {
    if (reviewModal.variant === "subscription") {
      if (response?.success) {
        const credits = buildRechargeCredits(quantities, categories);
        const creditedTests = credits.reduce(
          (sum, credit) => sum + Number(credit.quantity),
          0,
        );
        if (onRechargeSuccess) {
          onRechargeSuccess({ ...response, creditedTests, skipSuccessModal: true });
        } else if (onBack) {
          onBack();
        }
        closeReviewModal();
      }
      return;
    }

    if (response?.success) {
      const rechargeCredits = buildRechargeCredits(quantities, categories);
      const creditedTests = rechargeCredits.reduce(
        (sum, credit) => sum + (Number(credit.quantity) || 0),
        0,
      );
      if (onRechargeSuccess) {
        onRechargeSuccess({
          ...response,
          creditedTests,
          topupPlan: true,
          skipSuccessModal: true,
        });
      } else if (onBack) {
        onBack();
      }
      closeReviewModal();
    }
  };

  const subtitle = mode === "subscription"
    ? "Adjust your subscription or top up with additional test credits."
    : "Scale your testing capacity with credits that never expire.";

  const isPrepaid = mode !== "subscription";
  const isTestsSplitOpen = isPrepaid && testsDrawerOpen;

  return (
    <section
      className={`UsageMetricsPlanBuilder${isTestsSplitOpen ? " UsageMetricsPlanBuilder--testsOpen" : ""}`}
    >
      <div className="UsageMetricsPlanBuilder__Body">
        <div
          className={`UsageMetricsPlanBuilder__Main${isTestsSplitOpen ? " UsageMetricsPlanBuilder__Main--testsOpen" : ""}`}
        >
          <header className="UsageMetricsPlanBuilder__Header">
            <div className="UsageMetricsPlanBuilder__Heading">
              {onBack && (
                <Button
                  type="text"
                  icon={<LeftOutlined />}
                  onClick={onBack}
                  aria-label="Back to usage metrics"
                />
              )}
              <div>
                <h2 className="UsageMetricsPlanBuilder__Title">Build Your Own Plan</h2>
                <p className="UsageMetricsPlanBuilder__Subtitle">{subtitle}</p>
              </div>
            </div>
            {/* <span className="UsageMetricsPlanBuilder__PricingBadge">
              <DollarCircleOutlined />
              Unlock better pricing with larger orders.
            </span> */}
          </header>

          <div className="UsageMetricsPlanBuilder__Guidance">
            <p>
              <strong>Not sure which category you need?</strong>
              {" View tests in each category to find where the tests you need are included before purchasing."}
            </p>
            <Button
              type="link"
              icon={<EyeOutlined />}
              iconPosition="end"
              onClick={() => setTestsDrawerOpen(true)}
            >
              View tests
            </Button>
          </div>

          <div className="UsageMetricsPlanBuilder__Categories">
            {categoriesLoading && (
              <div className="UsageMetricsPlanBuilder__Loading">
                <Spin size="large" />
              </div>
            )}
            {!categoriesLoading && categories.length === 0 && (
              <p className="UsageMetricsPlanBuilder__Empty">
                No usage categories are available right now.
              </p>
            )}
            {!categoriesLoading && categories.map((category) => (
              <article className="UsageMetricsPlanBuilder__Category" key={category.key}>
                <div className="UsageMetricsPlanBuilder__CategoryHeader">
                  <h3 className="UsageMetricsPlanBuilder__CategoryTitle">
                    <span className={`UsageMetricsPlanBuilder__CategoryIcon ${category.accentClass}`}>
                      {CATEGORY_ICONS[category.key]}
                    </span>
                    {category.name}
                  </h3>
                  <div className="UsageMetricsPlanBuilder__ColumnLabels" aria-hidden>
                    <span>Quantity</span>
                    <span>Unit Price</span>
                    <span>Total Price</span>
                  </div>
                </div>

                <div className="UsageMetricsPlanBuilder__Rows">
                  {category.tests.map((test) => {
                    const maxQuantity = test.maximumAvailability;
                    const quantity = clampQuantity(quantities[test.key], maxQuantity);
                    const rowTotal = quantity * test.unitPrice;
                    const draftValue = quantityDrafts[test.key];
                    const inputValue = draftValue != null ? draftValue : String(quantity);
                    const handleQuantityChange = (value) => updateQuantity(test.key, value, maxQuantity);
                    const isRowDisabled = categoriesLoading || isCheckoutLoading || maxQuantity <= 0;

                    return (
                      <div className="UsageMetricsPlanBuilder__Row" key={test.key}>
                        <span className="UsageMetricsPlanBuilder__TestName">{test.name}</span>
                        <Slider
                          min={0}
                          max={maxQuantity > 0 ? maxQuantity : 1}
                          step={getQuantityStep(maxQuantity)}
                          marks={getSliderMarks(maxQuantity)}
                          tooltip={{ open: false }}
                          value={quantity}
                          onChange={handleQuantityChange}
                          className="UsageMetricsPlanBuilder__Slider"
                          disabled={isRowDisabled}
                        />
                        <AutoComplete
                          value={inputValue}
                          options={getQuantityOptions(maxQuantity)}
                          onSearch={(value) => handleQuantityDraftChange(test.key, value)}
                          onChange={(value) => handleQuantityDraftChange(test.key, value)}
                          onSelect={(value) => updateQuantity(test.key, value, maxQuantity)}
                          onBlur={() => commitQuantityDraft(test.key, maxQuantity)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") {
                              commitQuantityDraft(test.key, maxQuantity);
                            }
                          }}
                          className="UsageMetricsPlanBuilder__Quantity"
                          popupClassName="UsageMetricsPlanBuilder__QuantityDropdown"
                          disabled={isRowDisabled}
                          filterOption={false}
                          listHeight={84}
                          defaultActiveFirstOption
                          aria-label={`${category.name} ${test.name} quantity`}
                        />
                        <div className="UsageMetricsPlanBuilder__UnitPrice">
                          <span>{`× $${test.unitPrice.toFixed(2)}`}</span>
                        </div>
                        <span className="UsageMetricsPlanBuilder__Total">
                          {`$${rowTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>

          {!categoriesLoading && (mode === "subscription" ? (
            <SubscriptionPaymentSummary
              total={total}
              onTopUp={handleTopUp}
              onAddToSubscription={handleAddToSubscription}
            />
          ) : (
            <UsageMetricsPaymentSummary
              total={total}
              loading={isCheckoutLoading}
              onContinue={handlePrepaidContinue}
              onCancelPayment={resetCheckout}
              paymentUrl={paymentUrl}
            />
          ))}
        </div>

        {/* {mode === "subscription" && (
          <div className="UsageMetricsPlanBuilder__Aside">
            <UsageMetricsPlanOffers />
            <UsageMetricsTestsDrawer
              open={testsDrawerOpen}
              onClose={() => setTestsDrawerOpen(false)}
              overlay
            />
          </div>
        )} */}

        {/* {isPrepaid && ( */}
        <UsageMetricsTestsDrawer
          open={testsDrawerOpen}
          onClose={() => setTestsDrawerOpen(false)}
        />
        {/* )} */}
      </div>

      {mode === "subscription" && (
        <ReviewOneTimeTopUpModal
          open={reviewModal.open}
          variant={reviewModal.variant}
          onClose={closeReviewModal}
          onPay={handleReviewPay}
          categories={reviewCategories}
          credits={buildRechargeCredits(quantities, categories)}
          totalCredits={totalCredits}
          finalAmount={total}
          savings={0}
        />
      )}

      {isPrepaid && (
        <>
          <SuccessModal
            open={showPrepaidSuccessModal}
            onClose={handlePrepaidSuccessClose}
            title="Payment Successful!"
            description={`${Number(totalCredits).toLocaleString()} test credits have been added to your account.`}
          />
          <SuccessModal
            open={showPrepaidFailedModal}
            onClose={handlePrepaidFailedClose}
            variant="failed"
            title="Payment Failed"
            description="Your payment could not be completed. Please try again."
          />
        </>
      )}
    </section>
  );
}
